import React from 'react';
import { motion } from 'framer-motion';
import { LeafIcon, HeartIcon, TrendingUpIcon, CheckCircle2Icon, UsersIcon, UsersRoundIcon, CalendarCheckIcon } from 'lucide-react';
import { Container } from '../../components/ui/Container';
import { Button } from '../../components/ui/Button';
import { SubpageHero } from '../../components/sections/subpages/SubpageHero';
import { PatternDecor } from '../../components/ui/PatternDecor';
import { Link } from 'react-router-dom';
const pillars = [{
  icon: LeafIcon,
  title: 'Sustainability',
  body: 'Paid seats fund the day-to-day delivery of our programs, ensuring quality and consistency for every cohort.',
  bg: 'bg-sage-100'
}, {
  icon: HeartIcon,
  title: 'Social mission',
  body: 'Scholarships open access for families who need support — through transparent criteria and donor-backed seats.',
  bg: 'bg-[#F7EDE9]'
}, {
  icon: TrendingUpIcon,
  title: 'Scale',
  body: 'Institutional grants and partnerships allow us to reach more families across more regions over time.',
  bg: 'bg-sand-100'
}];
const types = [{
  title: 'Partial scholarships',
  body: 'For families with limited income or temporary financial difficulties.'
}, {
  title: 'Full scholarships',
  body: 'Priority support for vulnerable children, including disability and crisis situations.'
}, {
  title: 'Community-sponsored seats',
  body: 'Donor- and partner-funded places with clear criteria and reporting.'
}];
export function ScholarshipsPage() {
  return <>
      <SubpageHero eyebrow="Access for every child" title="Scholarships" paragraphs={['Education should be accessible, dignified, and sustainable.', 'Scholarships are supported through donations and institutional partnerships and are allocated transparently based on clear criteria.']} primaryCta={{
      label: 'Apply for a Scholarship',
      to: '/en/waitlist'
    }} secondaryCta={{
      label: 'View Programs',
      to: '/en/children/kids-programs'
    }} image="https://cdn.magicpatterns.com/uploads/8awkEnkAr35x3W2oQBLASf/Scholar-3.jpg" imageAlt="Young Muslim girl studying at a laptop in a bright, book-filled home study" compact masked data-id="element-3377" data-mp="1pma2k4mlrhil" />

      {/* Hybrid model */}
      <section className="py-16 md:py-24 bg-sage-50/60" data-id="element-3378" data-mp="2n7y5xx42gmbj">
        <Container data-id="element-3379" data-mp="2n7g14c3c8zs0">
          <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-3380" data-mp="2n5vhvimh539g">
            Our hybrid model
          </h2>
          <p className="mt-5 text-ink-700 leading-relaxed" data-id="element-3381" data-mp="2wwznhrq9fbrm">
            We use a simple structure: paid seats create sustainability,
            scholarships deliver social impact, and grants allow scale.
          </p>
          <div className="mt-10 grid md:grid-cols-3 gap-5" data-id="element-3382" data-mp="35z8u46ieqkzq">
            {pillars.map((p, i) => <motion.div key={p.title} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.5,
            delay: i * 0.08
          }} className={`rounded-2xl ${p.bg} border border-ink-900/5 p-6 shadow-soft`} data-id="element-3383" data-mp="3ayc0bua4ix9a" data-mpi={i}>
                <span className="w-11 h-11 inline-flex items-center justify-center rounded-full bg-white border border-forest-700/10 text-forest-700" data-id="element-3384" data-mp="2o9heexgrgf9">
                  <p.icon size={18} strokeWidth={1.6} data-id="element-3385" data-mp="3tzjbgxubzp6m" />
                </span>
                <h3 className="mt-5 font-display text-lg text-ink-900" data-id="element-3386" data-mp="3q023nxrfmfya">
                  {p.title}
                </h3>
                <p className="mt-3 text-ink-700 text-sm leading-relaxed" data-id="element-3387" data-mp="qen7hzwhn577">
                  {p.body}
                </p>
              </motion.div>)}
          </div>
        </Container>
      </section>

      {/* Pricing */}
      <section className="relative py-16 md:py-24 overflow-hidden isolate bg-white/60" data-id="element-3388" data-mp="b3fimt3phdpz">
        <PatternDecor side="left" y="top" size="lg" data-id="element-3389" data-mp="3t9abx3kxwl6n" />
        <Container data-id="element-3390" data-mp="3talitc9om91z">
          <div data-id="element-3391" data-mp="14zqqnwuu0ff">
            <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-3392" data-mp="14q2b4rgk1bu">
              Pricing
            </h2>
            <p className="mt-5 text-ink-700 leading-relaxed" data-id="element-3393" data-mp="ovlptpovsada">
              Transparent, family-friendly pricing — designed so paid seats
              sustain the program and scholarships open access for those who
              need them.
            </p>
          </div>

          {/* Standard tuition */}
          <div className="mt-12" data-id="element-3394" data-mp="k2j0jtr4zn2w">
            <h3 className="font-display text-2xl md:text-3xl text-ink-900" data-id="element-3395" data-mp="k1hdss0rdc5c">
              Standard tuition (monthly)
            </h3>
            <p className="mt-3 text-ink-700" data-id="element-3396" data-mp="1ckog1lxkzbje">
              <span className="font-semibold text-ink-900" data-id="element-3397" data-mp="1clg0c0lq9mud">Included:</span> Core
              Learning / Islamic & Cultural Foundation / Base Enrichment (1
              class)
            </p>

            <div className="mt-6 rounded-2xl bg-white border border-sage-200 shadow-soft overflow-hidden" data-id="element-3398" data-mp="2saxlrpa694th">
              <div className="grid grid-cols-[1fr_1.5fr_auto] gap-4 px-6 py-4 bg-sage-50 border-b border-sage-200 text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-900" data-id="element-3399" data-mp="36zlja13kvom2">
                <span data-id="element-3400" data-mp="2ec7q8e303d78">Age group</span>
                <span data-id="element-3401" data-mp="29cmgd8malkfq">Format</span>
                <span data-id="element-3402" data-mp="29de98boi942b">Price</span>
              </div>
              {[{
              age: '5–7',
              format: '2 classes / week',
              price: '€90'
            }, {
              age: '8–10',
              format: '2 classes (+1 free optional class) / week',
              price: '€120'
            }, {
              age: '11–14',
              format: '3 classes / week',
              price: '€150'
            }, {
              age: '15–18',
              format: '3 classes / week',
              price: '€180'
            }].map((row, i, arr) => <div key={row.age} className={`grid grid-cols-[1fr_1.5fr_auto] gap-4 px-6 py-5 items-center ${i < arr.length - 1 ? 'border-b border-sage-200' : ''}`} data-id="element-3403" data-mp="fmh18gd73v05" data-mpi={i}>
                  <span className="font-display text-lg text-ink-900" data-id="element-3404" data-mp="3j0jrnebtngvz">
                    {row.age}
                  </span>
                  <span className="text-ink-700" data-id="element-3405" data-mp="3t13wss2gp5jh">{row.format}</span>
                  <span className="font-display text-lg text-forest-700 font-semibold" data-id="element-3406" data-mp="3syr4pm2zpesp">
                    {row.price}
                  </span>
                </div>)}
            </div>
          </div>

          {/* Enrichment classes */}
          <div className="mt-14" data-id="element-3407" data-mp="35ag84v4bu1a8">
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-900" data-id="element-3408" data-mp="359exusv4xitk">
              Enrichment classes (optional)
            </h3>

            <div className="mt-5 rounded-2xl bg-white border border-sage-200 shadow-soft overflow-hidden" data-id="element-3409" data-mp="1ie07tfp40ijn">
              <div className="grid grid-cols-[1.5fr_1fr] gap-4 px-6 py-4 bg-sage-50 border-b border-sage-200 text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-900" data-id="element-3410" data-mp="1denlqx62bfi4">
                <span data-id="element-3411" data-mp="13h0xvl6scdm0">Class</span>
                <span data-id="element-3412" data-mp="13i2k8lt94mj1">Price</span>
              </div>
              {[{
              name: 'Additional language',
              price: '€25–40 / month'
            }, {
              name: 'Calligraphy',
              price: '€20–30 / month'
            }, {
              name: 'Islamic Arts & Crafts',
              price: '€30–50 / month'
            }].map((row, i, arr) => <div key={row.name} className={`grid grid-cols-[1.5fr_1fr] gap-4 px-6 py-5 items-center ${i < arr.length - 1 ? 'border-b border-sage-200' : ''}`} data-id="element-3413" data-mp="fsq04qwdyu1y" data-mpi={i}>
                  <span className="text-ink-900 font-medium" data-id="element-3414" data-mp="28m62lfebmj8g">{row.name}</span>
                  <span className="font-display text-lg text-forest-700 font-semibold" data-id="element-3415" data-mp="1uohjabzwjk5i">
                    {row.price}
                  </span>
                </div>)}
            </div>
          </div>

          {/* Family packages */}
          <div className="mt-14" data-id="element-3416" data-mp="1kvsc3tbdwsfz">
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-900" data-id="element-3417" data-mp="1pp4z5z0f0r6m">
              Family packages
            </h3>
            <p className="mt-3 text-ink-700 leading-relaxed" data-id="element-3418" data-mp="1byrsdzz9fiiv">
              Bringing more than one child? Committing for the year? We make it
              easier with built-in savings.
            </p>

            <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-id="element-3419" data-mp="11zsrx4onet6y">
              {[{
              icon: UsersIcon,
              bg: 'bg-sage-100',
              border: 'border-forest-700/10',
              eyebrow: 'Multi-child discount',
              value: '−15%',
              label: '2 children enrolled',
              note: 'Applied automatically to your monthly tuition.'
            }, {
              icon: UsersRoundIcon,
              bg: 'bg-[#F7EDE9]',
              border: 'border-[#E0BFB3]/40',
              eyebrow: 'Multi-child discount',
              value: '−25%',
              label: '3 or more children',
              note: 'Our best rate for larger families.'
            }, {
              icon: CalendarCheckIcon,
              bg: 'bg-sand-100',
              border: 'border-sand-300/50',
              eyebrow: 'Annual commitment',
              value: '2 months free',
              label: 'Pay for 10, get 12',
              note: 'Commit for the year and two months are on us.'
            }].map((pkg, i) => <motion.div key={pkg.label} initial={{
              opacity: 0,
              y: 16
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-60px'
            }} transition={{
              duration: 0.5,
              delay: i * 0.08,
              ease: [0.22, 1, 0.36, 1]
            }} className={`relative overflow-hidden rounded-2xl ${pkg.bg} border ${pkg.border} p-6 shadow-soft`} data-id="element-3420" data-mp="1168mear7pw03" data-mpi={i}>
                  <span className="w-12 h-12 inline-flex items-center justify-center rounded-xl bg-white border border-forest-700/10 text-forest-700" data-id="element-3421" data-mp="3vsswmxsw722u">
                    <pkg.icon size={22} strokeWidth={1.6} data-id="element-3422" data-mp="3qs4zdue2lp06" />
                  </span>
                  <p className="mt-5 text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700" data-id="element-3423" data-mp="26cqe5q4fanmx">
                    {pkg.eyebrow}
                  </p>
                  <p className="mt-2 font-display text-3xl md:text-4xl text-ink-900 leading-none" data-id="element-3424" data-mp="2fclvkcokneve">
                    {pkg.value}
                  </p>
                  <p className="mt-3 font-display text-lg text-ink-900" data-id="element-3425" data-mp="2k4n1zhz26bdi">
                    {pkg.label}
                  </p>
                  <p className="mt-2 text-ink-700 text-sm leading-relaxed" data-id="element-3426" data-mp="2p4j5zh7ra2t2">
                    {pkg.note}
                  </p>
                </motion.div>)}
            </div>

            <p className="mt-5 flex items-center gap-2 text-ink-500 text-sm" data-id="element-3427" data-mp="2yy8dgfbuar8z">
              <CheckCircle2Icon size={15} className="text-forest-700 flex-shrink-0" strokeWidth={1.8} data-id="element-3428" data-mp="331ekhp6s0mgi" />
              Discounts can be combined with scholarships where eligible.
            </p>
          </div>
        </Container>
      </section>

      {/* Types of support */}
      <section className="relative py-16 md:py-24 overflow-hidden isolate bg-[#EDF2F7]/60" data-id="element-3429" data-mp="26zpxh69m3los">
        <PatternDecor side="right" y="bottom" size="md" data-id="element-3430" data-mp="2722khxu88m8p" />
        <Container data-id="element-3431" data-mp="2l0af3z3sjbgp">
          <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-3432" data-mp="2l0tvvchpftt5">
            Types of support
          </h2>
          <p className="mt-5 text-ink-700 leading-relaxed" data-id="element-3433" data-mp="2uxf3m5l3zj0o">
            No child should be excluded from our programs because of financial
            circumstances.
          </p>
          <div className="mt-10 grid md:grid-cols-3 gap-5" data-id="element-3434" data-mp="2pwre5ocj3a8k">
            {types.map((t, i) => {
            const tones = ['bg-sage-100 border-forest-700/10', 'bg-[#F7EDE9] border-[#E0BFB3]/40', 'bg-sand-100 border-sand-300/40'];
            return <motion.div key={t.title} initial={{
              opacity: 0,
              y: 16
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-60px'
            }} transition={{
              duration: 0.5,
              delay: i * 0.08
            }} className={`rounded-2xl border p-6 shadow-soft ${tones[i % tones.length]}`} data-id="element-3435" data-mp="38pd7a0w1wmpg" data-mpi={i}>
                  <h3 className="font-display text-lg text-ink-900" data-id="element-3436" data-mp="3ebvugk6cc5y9">
                    {t.title}
                  </h3>
                  <p className="mt-3 text-ink-700 text-sm leading-relaxed" data-id="element-3437" data-mp="2zk4sm4zdrvrk">
                    {t.body}
                  </p>
                </motion.div>;
          })}
          </div>
        </Container>
      </section>

      {/* How allocation works */}
      <section className="py-16 md:py-24 bg-white/60" data-id="element-3438" data-mp="2vjkyzbe0naa6">
        <Container data-id="element-3439" data-mp="2njwzly0imj97">
          <div className="grid lg:grid-cols-12 gap-12 items-center" data-id="element-3440" data-mp="2nkgboy3hidfb">
            <div className="lg:col-span-6" data-id="element-3441" data-mp="2nlrfgxwxjl6f">
              <h2 className="font-display text-3xl md:text-4xl text-ink-900 leading-tight" data-id="element-3442" data-mp="2ilv94s0rfhnp">
                How allocation works
              </h2>
              <ul className="mt-6 space-y-4 text-ink-700" data-id="element-3443" data-mp="2drr0m4buj1ki">
                {['Families apply with a brief, confidential needs assessment', 'Decisions follow transparent, published criteria', 'Dignity is protected — no family is publicly identified', 'Outcomes are reported quarterly to donors and partners'].map(p => <li key={p} className="flex items-start gap-3" data-id="element-3444" data-mp="1qtxi3e2e0aeh">
                    <CheckCircle2Icon size={18} className="text-forest-700 mt-1 flex-shrink-0" strokeWidth={1.6} data-id="element-3445" data-mp="1qvh29fr5z5lj" />
                    <span data-id="element-3446" data-mp="3l5kmnsb2zp82">{p}</span>
                  </li>)}
              </ul>
              <div className="mt-8 flex flex-wrap gap-3" data-id="element-3447" data-mp="16zntdrg5gsa4">
                <Link to="/en/waitlist" data-id="element-3448" data-mp="1by7ejpmx601f">
                  <Button variant="primary" data-id="element-3449" data-mp="1bx60rdeiqzg0">Apply</Button>
                </Link>
                <Link to="/en/partnership/donate-for-children" data-id="element-3450" data-mp="1gr0n6olidsis">
                  <Button variant="secondary" data-id="element-3451" data-mp="1kru6f4o0aj5c">Support a Child</Button>
                </Link>
              </div>
            </div>
            <div className="lg:col-span-6" data-id="element-3452" data-mp="o2d4n0s59h8x">
              <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-card" data-id="element-3453" data-mp="t04wxvwpb5dn">
                <img src="https://cdn.magicpatterns.com/uploads/xvXUHwEJEt7AMzu9f6ER5q/Book.jpg" alt="Young Muslim boy writing notes while studying a textbook" className="w-full h-full object-cover" data-id="element-3454" data-mp="t290gcqou2de" />
              </div>
            </div>
          </div>
        </Container>
      </section>
    </>;
}